import buildHomeScopeKey from '@UI/Pages/Home/HomeHeader/HomeFilters/Helpers/buildHomeScopeKey';
import type { HomeScope } from '@UI/Pages/Home/Signals/homeScopeSignal';
import { DEFAULT_HOME_SCOPE } from '@UI/Pages/Home/Signals/homeScopeSignal';
import type { ComposedOrganization } from '@UI/Providers/CurrentOrganizationProvider/helpers/composeAssignedOrganizations';

export type ScopeOption = {
	readonly key: string;
	readonly label: string;
	readonly scope: HomeScope;
};

export type ScopeOptionGroup = {
	readonly label: string;
	readonly items: readonly ScopeOption[];
};

export type ScopeOptions = {
	/** Empty unless the caller has at least two agencies: a single choice is not a filter. */
	readonly groups: readonly ScopeOptionGroup[];
	readonly facilityCount: number;
	/** Set when exactly one agency exists in total. */
	readonly onlyFacilityName: string | null;
};

const compareNames = (left: { readonly name: string }, right: { readonly name: string }): number =>
	left.name.localeCompare(right.name, 'fr');

const buildOption = (scope: HomeScope, label: string): ScopeOption => ({
	key: buildHomeScopeKey(scope),
	label,
	scope,
});

/**
 * Builds the Périmètre options from the caller's organizations only: the whole perimeter in its own
 * leading group, then one group per organization (an "all my agencies of X" entry when it holds at
 * least two, then its agencies).
 */
const buildScopeOptions = (organizations: readonly ComposedOrganization[]): ScopeOptions => {
	const populated = organizations
		.filter((organization) => organization.facilities.length > 0)
		.sort(compareNames);
	const facilityCount = populated.reduce(
		(total, organization) => total + organization.facilities.length,
		0,
	);
	const onlyFacility = facilityCount === 1 ? (populated[0]?.facilities[0] ?? null) : null;

	if (facilityCount < 2) {
		return { groups: [], facilityCount, onlyFacilityName: onlyFacility?.name ?? null };
	}

	const organizationGroups = populated.map<ScopeOptionGroup>((organization) => {
		const facilityOptions = [...organization.facilities]
			.sort(compareNames)
			.map((facility) => buildOption({ kind: 'facility', id: facility.id }, facility.name));
		const organizationOption =
			organization.facilities.length >= 2
				? [
						buildOption(
							{ kind: 'organization', id: organization.id },
							`Mes agences de ${organization.name} (${organization.facilities.length})`,
						),
					]
				: [];

		return { label: organization.name, items: [...organizationOption, ...facilityOptions] };
	});

	return {
		groups: [
			{
				label: '',
				items: [buildOption(DEFAULT_HOME_SCOPE, `Tout mon périmètre (${facilityCount} agences)`)],
			},
			...organizationGroups,
		],
		facilityCount,
		onlyFacilityName: null,
	};
};

export default buildScopeOptions;
